import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  margin-top: 1rem;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  input {
    accent-color: #0077cc;
  }
`;
const Titulo = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: #222;
`;

const CidadeBloco = styled.div`
  margin-bottom: 1rem;
`;

const CidadeHeader = styled.div`
  background-color: #f0f4fa;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input {
    accent-color: #0077cc;
    pointer-events: auto;
  }
`;

const EmpreendimentosLista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.6rem 1.5rem;
`;

const CheckboxLabel = styled.label`
  background-color: #f9fbff;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;

  input {
    accent-color: #0077cc;
  }

  &:hover {
    border: 1px solid #0077cc;
    background-color: #e8f3ff;
  }
`;

interface Empreendimento {
  id: number;
  nome: string;
  nomeCidade: string;
  abreviacaoCidade: string;
}

interface LocalizacaoProps {
  empreendimentos: Empreendimento[];
  selecionados: any[];
  empreendsSelecionados: any[];
  setSelecionados: React.Dispatch<React.SetStateAction<any[]>>;
  setBloquearBtn: (btn: boolean) => void;
}

export default function Localizacao({
  empreendimentos,
  selecionados,
  empreendsSelecionados,
  setSelecionados,
  setBloquearBtn,
}: LocalizacaoProps) {
  const [cidadesAbertas, setCidadesAbertas] = useState<string[]>([]);

  // Função separada para agrupar empreendimentos por cidade
  const agruparPorCidadeEstado = (
    lista: Empreendimento[]
  ): Record<string, Empreendimento[]> => {
    return lista.reduce((acc, emp) => {
      const chave = `${emp.nomeCidade} - ${emp.abreviacaoCidade}`;
      if (!acc[chave]) acc[chave] = [];
      acc[chave].push(emp);
      return acc;
    }, {} as Record<string, Empreendimento[]>);
  };

  const agrupados = useMemo(
    () => agruparPorCidadeEstado(empreendimentos),
    [empreendimentos]
  );

  const toggleEmpreendimento = (emp: Empreendimento) => {
    setSelecionados(
      (prev) =>
        prev.some((e) => e.id === emp.id)
          ? prev.filter((e) => e.id !== emp.id)
          : [...prev, emp] // armazena o objeto inteiro
    );
  };

  const toggleCidadeAberta = (cidade: string) => {
    setCidadesAbertas((prev) =>
      prev.includes(cidade)
        ? prev.filter((c) => c !== cidade)
        : [...prev, cidade]
    );
  };

  const marcarCidade = (cidade: string, marcar: boolean) => {
    const empreendimentosCidade = agrupados[cidade];
    setSelecionados((prev) => {
      if (marcar) {
        // adiciona apenas os que ainda não estão selecionados
        const novos = empreendimentosCidade.filter(
          (e) => !prev.some((sel) => sel.id === e.id)
        );
        return [...prev, ...novos];
      } else {
        // remove todos da cidade
        return prev.filter(
          (sel) => !empreendimentosCidade.some((e) => e.id === sel.id)
        );
      }
    });
  };

  const marcarTodos = () => {
    const todosSelecionados = empreendimentos.every((e) =>
      selecionados.some((sel) => sel.id === e.id)
    );
    setSelecionados(todosSelecionados ? [] : [...empreendimentos]);
  };

  // Bloquear ou desbloquear botão
  useEffect(() => {
    setBloquearBtn(selecionados.length === 0);
  }, [selecionados, setBloquearBtn]);

  // Atualiza selecionados quando empreendsSelecionados mudar
  useEffect(() => {
    if (empreendsSelecionados && empreendsSelecionados.length > 0) {
      const selecionadosCompletos = empreendimentos.filter((emp) =>
        empreendsSelecionados.some((e) => e.empreendId === emp.id)
      );
      setSelecionados(selecionadosCompletos);
    } else {
      setSelecionados([]);
    }
  }, [empreendsSelecionados, empreendimentos, setSelecionados]);

  return (
    <Wrapper>
       <Titulo> Para onde gostaria de ir? (pode ser mais de um)</Titulo>
      <Header>
        <input
          type="checkbox"
          checked={empreendimentos.every((e) =>
            selecionados.some((sel) => sel.id === e.id)
          )}
          onChange={marcarTodos}
        />
        <strong>Marcar todos os empreendimentos</strong>
      </Header>

      {Object.entries(agrupados).map(([cidade, lista]) => {
        const todosSelecionados = lista.every((e) =>
          selecionados.some((sel) => sel.id === e.id)
        );
        const algunsSelecionados = lista.some((e) =>
          selecionados.some((sel) => sel.id === e.id)
        );
        const isExpandido = cidadesAbertas.includes(cidade);

        return (
          <CidadeBloco key={cidade}>
            <CidadeHeader onClick={() => toggleCidadeAberta(cidade)}>
              <input
                type="checkbox"
                checked={todosSelecionados}
                ref={(el) => {
                  if (el)
                    el.indeterminate = algunsSelecionados && !todosSelecionados;
                }}
                onChange={(e) => marcarCidade(cidade, e.target.checked)}
              />
              <span>
                {isExpandido ? "▼" : "▶"} {cidade}
              </span>
            </CidadeHeader>

            {isExpandido && (
              <EmpreendimentosLista>
                {lista.map((emp) => (
                  <CheckboxLabel key={emp.id}>
                    <input
                      type="checkbox"
                      checked={selecionados.some((e) => e.id === emp.id)}
                      onChange={() => toggleEmpreendimento(emp)}
                    />
                    {emp.nome}
                  </CheckboxLabel>
                ))}
              </EmpreendimentosLista>
            )}
          </CidadeBloco>
        );
      })}
    </Wrapper>
  );
}
